import { Eye } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../../components/ui/ThreeDotsMenu";
import { formatCurrency, formatDate } from "../../../lib/utils";
import {
  Dispatch,
  DispatchSearchRequest,
  DispatchSearchResponse,
} from "../@types";
import { useSearchDispatchesMutation } from "../dispatchApiSlice";
import DispatchHistoryModal from "./DispatchHistoryModal";
import DispatchFilterComponent from "./ui/DispatchFilterComponent";

interface VehicleDispatchHistoryProps {
  id?: string;
  idType: string;
}

const VehicleDispatchHistory: React.FC<VehicleDispatchHistoryProps> = ({
  id,
  idType,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [viewDispatch, setViewDispatch] = useState<Dispatch | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);

  const [filterData, setFilterData] = useState({
    startDate: "",
    endDate: "",
    category: "",
  });

  // RTK Query mutation
  const [getDispatchHistory, { data: dispatchHistoryData, isLoading }] =
    useSearchDispatchesMutation();

  const fetchDispatchHistory = useCallback(async () => {
    const requestBody: DispatchSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      startDate: filterData.startDate || undefined,
      endDate: filterData.endDate || undefined,
      vehicleId: idType === "vehicle" ? id : undefined,
      driverId: idType === "driver" ? id : undefined,
    };

    try {
      await getDispatchHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch dispatch history:", err);
    }
  }, [currentPage, pageSize, filterData, getDispatchHistory]);

  useEffect(() => {
    fetchDispatchHistory();
  }, [currentPage, pageSize, filterData, fetchDispatchHistory]);

  const columns: Column<Dispatch>[] = [
    {
      key: "tripStartTime",
      header: "Date",

      render: (value: string) => (
        <span className="text-hColor capitalize">{formatDate(value)}</span>
      ),
    },
    {
      key: "crews",
      header: "Crew member",

      render: (value: any, item: Dispatch) => (
        <span className="text-hColor">
          {item.crews?.length
            ? `${item.crews[0].name}, +${item.crews.length} more`
            : "No crew"}
        </span>
      ),
    },
    {
      key: "totalExpense",
      header: "Expenses (NGN)",

      render: (value: number) => (
        <span className="text-hColor">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "totalIncome",
      header: "Income (NGN)",

      render: (value: number) => (
        <span className="text-hColor">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "approximateDistance",
      header: "Distance (KM)",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: Dispatch) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View dispatch",
                onClick: () => {
                  setViewDispatch(item);
                  setOpenModal("view");
                },
                icon: <Eye className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = dispatchHistoryData?.data as
    | DispatchSearchResponse
    | undefined;

  return (
    <div>
      <div className="space-y-6 bg-white rounded-[10px] p-4">
        {/* Filters and Actions */}
        <div className="flex items-center space-x-2">
          <h1>Dispatch history</h1>
          <DispatchFilterComponent onApplyFilters={setFilterData} />
        </div>

        {/* Table */}
        <div className="overflow-hidden space-y-6">
          <Table
            data={response?.data || []}
            columns={columns}
            loading={isLoading}
            emptyMessage="No dispatch history found"
          />
        </div>
      </div>
      <div className="">
        {/* Pagination */}
        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      {openModal === "view" && viewDispatch && (
        <DispatchHistoryModal
          dispatch={viewDispatch}
          isOpen={openModal === "view"}
          onClose={() => setOpenModal("")}
        />
      )}
    </div>
  );
};

export default VehicleDispatchHistory;
